import type { RichBlock, RichBlockType, RichInline, RichStyleName } from './types';
import type { BlockIds } from './ids';

type Node = Record<string, unknown>;

const STYLE_BY_PLATE_MARK: Record<string, RichStyleName> = {
  bold: 'bold',
  italic: 'italic',
  underline: 'underline',
  strikethrough: 'strike',
  code: 'code',
};

const HEADING_LEVEL_BY_TYPE: Record<string, number> = { h1: 1, h2: 2, h3: 3 };

const ORDERED_LIST_STYLES = new Set([
  'decimal',
  'lower-alpha',
  'upper-alpha',
  'lower-roman',
  'upper-roman',
  'lower-greek',
  'upper-greek',
]);

export function isNode(value: unknown): value is Node {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function childNodes(node: Node): unknown[] {
  return Array.isArray(node.children) ? node.children : [];
}

function plateMarks(node: Node): Partial<Record<RichStyleName, true>> {
  const styles: Partial<Record<RichStyleName, true>> = {};
  for (const [mark, style] of Object.entries(STYLE_BY_PLATE_MARK)) {
    if (node[mark] === true) {
      styles[style] = true;
    }
  }
  return styles;
}

function plateInline(children: unknown[], inherited: Partial<Record<RichStyleName, true>>): RichInline[] {
  const out: RichInline[] = [];
  for (const child of children) {
    if (!isNode(child)) {
      continue;
    }
    if (typeof child.text === 'string') {
      if (child.text.length > 0) {
        out.push({ type: 'text', text: child.text, styles: { ...inherited, ...plateMarks(child) } });
      }
    } else if (child.type === 'mention' && typeof child.value === 'string') {
      out.push({ type: 'mention', props: { handle: child.value } });
    } else if (child.type === 'a' && typeof child.url === 'string') {
      out.push({ type: 'link', href: child.url, content: plateInline(childNodes(child), inherited) });
    } else {
      out.push(...plateInline(childNodes(child), inherited));
    }
  }
  return out;
}

function leafTexts(node: Node): string[] {
  if (typeof node.text === 'string') {
    return [node.text];
  }
  return childNodes(node).flatMap((child) => (isNode(child) ? leafTexts(child) : []));
}

function flatText(node: Node): string {
  return leafTexts(node).join(' ').replace(/\s+/g, ' ').trim();
}

function listType(node: Node): RichBlockType | null {
  if (typeof node.listStyleType !== 'string') {
    return null;
  }
  if (node.listStyleType === 'todo') {
    return 'checkListItem';
  }
  return ORDERED_LIST_STYLES.has(node.listStyleType) ? 'numberedListItem' : 'bulletListItem';
}

function codeBlock(node: Node, id: string): RichBlock {
  const lines = childNodes(node).map((line) => (isNode(line) ? leafTexts(line).join('') : ''));
  const text = lines.length > 0 ? lines.join('\n') : flatText(node);
  const props: RichBlock['props'] = typeof node.lang === 'string' && node.lang.length > 0 ? { language: node.lang } : {};
  return {
    id,
    type: 'codeBlock',
    props,
    content: text.length > 0 ? [{ type: 'text', text, styles: {} }] : [],
    children: [],
  };
}

function plateBlock(node: Node, id: string): RichBlock {
  const list = listType(node);
  if (list !== null) {
    const props: RichBlock['props'] = list === 'checkListItem' ? { checked: node.checked === true } : {};
    return { id, type: list, props, content: plateInline(childNodes(node), {}), children: [] };
  }
  if (node.type === 'code_block') {
    return codeBlock(node, id);
  }
  const level = typeof node.type === 'string' ? HEADING_LEVEL_BY_TYPE[node.type] : undefined;
  if (level !== undefined) {
    return { id, type: 'heading', props: { level }, content: plateInline(childNodes(node), {}), children: [] };
  }
  if (node.type === 'blockquote') {
    return { id, type: 'quote', props: {}, content: plateInline(childNodes(node), {}), children: [] };
  }
  if (node.type === 'p') {
    return { id, type: 'paragraph', props: {}, content: plateInline(childNodes(node), {}), children: [] };
  }
  const text = flatText(node);
  return {
    id,
    type: 'paragraph',
    props: {},
    content: text.length > 0 ? [{ type: 'text', text, styles: {} }] : [],
    children: [],
  };
}

export interface PlateListEntry {
  indent: number;
  block: RichBlock;
}

export function placePlateBlock(
  node: Node,
  ids: BlockIds,
  out: RichBlock[],
  stack: PlateListEntry[],
): PlateListEntry[] {
  const block = plateBlock(node, ids.take(node.id));
  if (listType(node) === null) {
    out.push(block);
    return [];
  }
  const indent = typeof node.indent === 'number' ? node.indent : 1;
  const open = stack.filter((entry) => entry.indent < indent);
  const parent = open[open.length - 1];
  (parent === undefined ? out : parent.block.children).push(block);
  return [...open, { indent, block }];
}
